import type { ContextPayload } from './types'

/**
 * Capture host-page context to hand to the widget. The widget already reads
 * `document.documentElement.dataset.vyroChatPageVehicleId` / `...VehicleCode`
 * (see packages/chat `readPageVehicleContext`); here we read the equivalent
 * dataset on the *host* page so a dealer who tags their VDP gets vehicle-aware
 * chat. Everything is best-effort and read-only.
 */
export function captureContext(win: Window): ContextPayload {
  const doc = win.document
  const root = doc.documentElement?.dataset ?? {}
  return {
    url: win.location.href,
    title: doc.title ?? '',
    referrer: doc.referrer ?? '',
    vehicleId: root.vyroChatPageVehicleId?.trim() || null,
    vehicleCode: root.vyroChatPageVehicleCode?.trim() || null,
  }
}

/**
 * Fire `callback` whenever the host page navigates client-side. ICP dealer
 * sites are SPAs (Next.js), so `history.pushState` / `replaceState` and
 * `popstate` must all re-push context. Returns a teardown function that
 * restores the patched History methods.
 */
export function onNavigation(win: Window, callback: () => void): () => void {
  const history = win.history
  // Keep the pristine (unbound) references so teardown restores exactly what
  // was there — never a wrapper — even across repeated init/teardown cycles.
  const originalPush = history.pushState
  const originalReplace = history.replaceState

  history.pushState = function patchedPushState(
    this: History,
    ...args: Parameters<History['pushState']>
  ) {
    const result = originalPush.apply(this, args)
    callback()
    return result
  }
  history.replaceState = function patchedReplaceState(
    this: History,
    ...args: Parameters<History['replaceState']>
  ) {
    const result = originalReplace.apply(this, args)
    callback()
    return result
  }

  win.addEventListener('popstate', callback)

  return () => {
    history.pushState = originalPush
    history.replaceState = originalReplace
    win.removeEventListener('popstate', callback)
  }
}
